import {
  ApiProperty,
  ApiPropertyOptional,
  IsEmail,
  IsInt,
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Max,
  Min,
  Type,
} from './decorators-nest';

const DATE_ISO_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/** Barème des points */
export enum PointsEventType {
  /** +1 — jour de suivi consécutif (automatique, déclenché par la saisie de la journée) */
  DAILY_STREAK = 'DAILY_STREAK',

  /** +5 — saisie de sa journée (humeur, symptômes, cycle) */
  DAILY_ENTRY = 'DAILY_ENTRY',

  /** +30 — cycle complet enregistré */
  CYCLE_COMPLETED = 'CYCLE_COMPLETED',

  /** +15 — fenêtre de fertilité identifiée */
  FERTILE_WINDOW = 'FERTILE_WINDOW',

  /** +2 — recette sélectionnée (favori / panier) */
  RECIPE_SELECTED = 'RECIPE_SELECTED',

  /**
   * +15 par défaut — sondage / questionnaire complété (créé uniquement par
   * POST /gamification/survey/completed, jamais par l'app).
   * referenceId = identifiant du sondage ('sondage-satisfaction-2026-08'),
   * ce qui limite le gain à une fois par sondage et par patiente.
   */
  SURVEY_COMPLETED = 'SURVEY_COMPLETED',

  /** 0 — consultation débloquée. referenceId = 'streak-N' ou 'points-N' (origine + numéro) */
  CONSULTATION_UNLOCKED = 'CONSULTATION_UNLOCKED',

  /** -30 ou -300 — consultation utilisée (déduit selon l'origine du déblocage) */
  CONSULTATION_USED = 'CONSULTATION_USED',
}

/** Points gagnés par type d'événement */
export const POINTS_VALUES = {
  [PointsEventType.DAILY_STREAK]: 1,
  [PointsEventType.DAILY_ENTRY]: 5,
  [PointsEventType.CYCLE_COMPLETED]: 30,
  [PointsEventType.FERTILE_WINDOW]: 15,
  [PointsEventType.RECIPE_SELECTED]: 2,
  [PointsEventType.SURVEY_COMPLETED]: 15,
  [PointsEventType.CONSULTATION_UNLOCKED]: 0,
} as const;

/** Coût d'une consultation selon l'origine du déblocage */
export const CONSULTATION_COSTS = {
  streak: 30, // débloquée par 30 jours de suivi consécutifs
  points: 300, // débloquée par le cumul de points
} as const;

/** Origine d'un déblocage de consultation */
export type ConsultationOrigin = keyof typeof CONSULTATION_COSTS;

/** Seuils de déblocage */
export const UNLOCK_GOALS = {
  points: 300,
  streakDays: 30,
} as const;

export class CreateRecordDto {
  @ApiProperty({ example: 'd2b34b79-49ae-4c0c-9f70-4fe26bb21462' })
  @IsUUID()
  patientId: string;

  @ApiProperty({ example: 'DAILY_STREAK', description: 'Point Event Name' })
  @IsString()
  @IsNotEmpty()
  type: PointsEventType;

  @ApiProperty({ example: 'YYYY-MM-DD', description: 'Event reference id' })
  @IsString()
  @IsNotEmpty()
  referenceId: string;

  @ApiPropertyOptional({ example: '2026-07-22', description: "Patient's local date (YYYY-MM-DD)" })
  @IsOptional()
  @Matches(DATE_ISO_REGEX)
  date?: string;
}

/** Résumé gamification renvoyé par GET /gamification/patient/:id/summary */
export interface GamificationSummaryDto {
  totalPoints: number;
  streakDays: number;
  goalPoints: number;
  goalStreakDays: number;
  consultationAvailable: boolean;
  consultationOrigin: ConsultationOrigin | null;
  consultationsUnlocked: number;
}

export interface PointsEventDto {
  id: string;
  type: PointsEventType;
  referenceId: string;
  points: number;
  date: string;
}

/**
 * Plafond de points pour un sondage — garde-fou sur une valeur qui arrive
 * de l'extérieur (Zapier / script), et non du barème figé.
 */
export const MAX_SURVEY_POINTS = 100;

/**
 * Corps de POST /gamification/survey/completed — appelé par Zapier (ou un
 * script) à chaque nouvelle réponse arrivée dans le Google Sheet du sondage.
 * Pas de champ `date` : la patiente est créditée à la date du serveur, ce qui
 * évite le refus des dates passées (une ligne de Sheet peut être rejouée
 * plusieurs jours après la réponse).
 */
export class SurveyCompletedDto {
  @ApiProperty({ example: 'patiente@example.com', description: 'Email saisi dans le sondage' })
  @IsEmail()
  email: string;

  @ApiProperty({
    example: 'sondage-satisfaction-2026-09',
    description: 'Identifiant du sondage — un seul gain par sondage et par patiente',
  })
  @IsString()
  @IsNotEmpty()
  referenceId: string;

  @ApiPropertyOptional({
    example: 15,
    description: `Points à créditer (${POINTS_VALUES[PointsEventType.SURVEY_COMPLETED]} par défaut, ${MAX_SURVEY_POINTS} max)`,
  })
  @IsOptional()
  // Zapier envoie volontiers une cellule de Sheet en chaîne ('30') : converti
  // avant validation, sinon @IsInt rejetterait l'appel
  @Type(() => Number)
  @IsInt()
  @Min(1)
  @Max(MAX_SURVEY_POINTS)
  points?: number;
}

/** Issue d'un appel à POST /gamification/survey/completed */
export type SurveyCompletedStatus =
  /** points crédités */
  | 'credited'
  /** ce sondage avait déjà été récompensé pour cette patiente (ligne rejouée) */
  | 'already_credited'
  /** aucune patiente ne porte cet email — à rattraper à la main */
  | 'patient_not_found';

export interface SurveyCompletedResultDto {
  status: SurveyCompletedStatus;
  /** Points crédités par cet appel — 0 si déjà récompensé ou patiente inconnue */
  points: number;
}

export class UseConsultationDto {
  @ApiProperty({ example: 'd2b34b79-49ae-4c0c-9f70-4fe26bb21462' })
  @IsUUID()
  patientId: string;

  @ApiPropertyOptional({ example: '2026-07-22', description: "Patient's local date (YYYY-MM-DD)" })
  @IsOptional()
  @Matches(DATE_ISO_REGEX)
  date?: string;
}
